import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ExpenseForm from '../components/ExpenseForm';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalError } from '../utils/alert';
import './AccountsPage.css';
import '../components/MobileApp.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return new Intl.DateTimeFormat('pt-BR').format(date);
};

const getStatusClass = (status) => {
  if (status === 'Paga') return 'paga';
  if (status === 'Pendente') return 'pendente';
  if (status === 'Atrasada') return 'atrasada';
  return '';
};

const decodePaginationLabel = (label) =>
  label.replace(/&laquo;/g, '«').replace(/&raquo;/g, '»').replace(/&amp;/g, '&');

function AccountsPayablePage() {
  const [view, setView] = useState('list');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({
    description: '',
    due_date_start: '',
    due_date_end: '',
    status: '',
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 768;

  // Fetch provider summaries
  useEffect(() => {
    setLoading(true);
    api.get('/accounts/payable')
      .then(res => setProviders(res.data))
      .catch(() => swalError('Erro ao carregar contas a pagar.'))
      .finally(() => setLoading(false));
  }, [refreshTrigger]);

  // Fetch expenses for selected provider
  const fetchExpenses = (url = '/expenses', currentFilters = filters) => {
    if (!selectedProvider) return;
    const urlObj = new URL(url, window.location.origin);
    const page = urlObj.searchParams.get('page');
    const params = new URLSearchParams({ ...currentFilters, provider_id: selectedProvider.provider_id });
    if (page) params.set('page', page);
    const pathname = urlObj.pathname.replace(/^\/api/, '');
    api.get(`${pathname}?${params.toString()}`)
      .then(res => {
        setExpenses(res.data.data);
        setPagination(res.data);
      })
      .catch(() => swalError('Erro ao carregar despesas.'));
  };

  useEffect(() => {
    if (view === 'detail' && selectedProvider) {
      fetchExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedProvider, refreshTrigger]);

  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    setView('detail');
    setFilters({ description: '', due_date_start: '', due_date_end: '', status: '' });
    setShowForm(false);
    setEditingExpense(null);
    setExpenses([]);
  };

  const handleBack = () => {
    setView('list');
    setSelectedProvider(null);
    setExpenses([]);
    setPagination(null);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleApplyFilters = () => {
    fetchExpenses('/expenses');
    setShowFilterModal(false);
  };

  const handleClearFilters = () => {
    const cleared = { description: '', due_date_start: '', due_date_end: '', status: '' };
    setFilters(cleared);
    fetchExpenses('/expenses', cleared);
    setShowFilterModal(false);
  };

  const handleNewExpense = () => {
    setEditingExpense(null);
    setShowForm(true);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setShowForm(true);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingExpense(null);
    setRefreshTrigger(prev => prev + 1);
  };

  // Global summary totals
  const globalTotal = providers.reduce((s, p) => s + parseFloat(p.total || 0), 0);
  const globalPaid = providers.reduce((s, p) => s + parseFloat(p.total_paid || 0), 0);
  const globalOpen = providers.reduce((s, p) => s + parseFloat(p.total_open || 0), 0);
  const globalOverdue = providers.reduce((s, p) => s + parseFloat(p.total_overdue || 0), 0);

  // ─── SHARED COMPONENTS ──────────────────────────────────────────

  const renderSummaryCards = (total, paid, open, overdue, countPaid, countOpen) => (
    <div className="acct-cards">
      <div className="acct-card acct-card-total">
        <div className="acct-card-label">Total Geral</div>
        <div className="acct-card-value">{formatCurrency(total)}</div>
      </div>
      <div className="acct-card acct-card-paid acct-card-payable">
        <div className="acct-card-label">Pago</div>
        <div className="acct-card-value">{formatCurrency(paid)}</div>
        {countPaid != null && <div className="acct-card-sub">{countPaid} lançamento(s)</div>}
      </div>
      <div className="acct-card acct-card-open">
        <div className="acct-card-label">Em Aberto</div>
        <div className="acct-card-value">{formatCurrency(open)}</div>
        {countOpen != null && <div className="acct-card-sub">{countOpen} lançamento(s)</div>}
      </div>
      <div className="acct-card acct-card-overdue">
        <div className="acct-card-label">Em Atraso</div>
        <div className="acct-card-value">{formatCurrency(overdue)}</div>
      </div>
    </div>
  );

  const renderPagination = () => {
    if (!pagination?.links) return null;
    return (
      <div className="acct-pagination">
        <ul className="pagination">
          {pagination.links.map((link, i) => (
            <li key={i} className={`page-item ${link.active ? 'active' : ''} ${!link.url ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => link.url && fetchExpenses(link.url)}>
                {decodePaginationLabel(link.label)}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  // ─── FILTERS ────────────────────────────────────────────────────

  const renderFilters = () => (
    <div className="acct-filters">
      <div className="acct-filter-group">
        <label>Descrição</label>
        <input type="text" name="description" value={filters.description} onChange={handleFilterChange} className="acct-filter-input" placeholder="Buscar..." autoComplete="off" />
      </div>
      <div className="acct-filter-group">
        <label>Vencimento De</label>
        <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="acct-filter-input" />
      </div>
      <div className="acct-filter-group">
        <label>Vencimento Até</label>
        <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="acct-filter-input" />
      </div>
      <div className="acct-filter-group">
        <label>Status</label>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="acct-filter-select">
          <option value="">Todos</option>
          <option value="Pendente">Pendente</option>
          <option value="Paga">Paga</option>
          <option value="Atrasada">Atrasada</option>
        </select>
      </div>
      <div className="acct-filter-actions">
        <button className="acct-btn acct-btn-primary" onClick={handleApplyFilters}>Aplicar</button>
        <button className="acct-btn acct-btn-outline" onClick={handleClearFilters}>Limpar</button>
      </div>
    </div>
  );

  const renderMobileFilters = () => (
    <div style={{ padding: '0 1.2rem 1rem' }}>
      <div className="mapp-filter-field">
        <label className="mapp-filter-label">Descrição</label>
        <input type="text" name="description" value={filters.description} onChange={handleFilterChange} className="mapp-filter-input" placeholder="Buscar..." autoComplete="off" />
      </div>
      <div className="mapp-filter-row">
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Vencimento De</label>
          <input type="date" name="due_date_start" value={filters.due_date_start} onChange={handleFilterChange} className="mapp-filter-input" />
        </div>
        <div className="mapp-filter-field">
          <label className="mapp-filter-label">Vencimento Até</label>
          <input type="date" name="due_date_end" value={filters.due_date_end} onChange={handleFilterChange} className="mapp-filter-input" />
        </div>
      </div>
      <div className="mapp-filter-field">
        <label className="mapp-filter-label">Status</label>
        <select name="status" value={filters.status} onChange={handleFilterChange} className="mapp-filter-select">
          <option value="">Todos</option>
          <option value="Pendente">Pendente</option>
          <option value="Paga">Paga</option>
          <option value="Atrasada">Atrasada</option>
        </select>
      </div>
      <div className="mapp-filter-actions">
        <button className="mapp-btn-apply" onClick={handleApplyFilters}>Aplicar</button>
        <button className="mapp-btn-clear" onClick={handleClearFilters}>Limpar</button>
      </div>
    </div>
  );

  // ─── FORM WRAPPER ────────────────────────────────────────────────

  const renderForm = () => {
    const initialValues = { provider_id: selectedProvider?.provider_id };
    if (isMobile) {
      return (
        <>
          <div className="mapp-sheet-overlay" onClick={() => setShowForm(false)} />
          <div className="mapp-sheet">
            <div className="mapp-sheet-handle" />
            <div className="mapp-sheet-header">
              <span className="mapp-sheet-title">{editingExpense ? 'Editar Despesa' : 'Nova Despesa'}</span>
              <button className="mapp-sheet-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="mapp-sheet-body">
              <ExpenseForm expense={editingExpense} onSuccess={handleSuccess} initialValues={initialValues} />
            </div>
          </div>
        </>
      );
    }
    return (
      <div className="acct-form-wrapper">
        <div className="acct-form-header">
          <span className="acct-form-title">{editingExpense ? 'Editar Despesa' : 'Nova Despesa'}</span>
          <button className="acct-btn acct-btn-outline" onClick={() => setShowForm(false)}>✕ Cancelar</button>
        </div>
        <ExpenseForm expense={editingExpense} onSuccess={handleSuccess} initialValues={initialValues} />
      </div>
    );
  };

  // ─── LIST VIEW ───────────────────────────────────────────────────

  const renderDesktopListView = () => (
    <div>
      <div className="acct-page-header">
        <div>
          <h1 className="acct-page-title">Contas a Pagar</h1>
          <p className="acct-page-sub">Despesas agrupadas por fornecedor</p>
        </div>
      </div>

      {loading ? (
        <div className="acct-loading">Carregando...</div>
      ) : (
        <>
          {renderSummaryCards(globalTotal, globalPaid, globalOpen, globalOverdue)}

          <div className="acct-table-wrapper">
            <table className="acct-table">
              <thead>
                <tr>
                  <th>Fornecedor</th>
                  <th className="text-end">Total</th>
                  <th className="text-end">Pago</th>
                  <th className="text-end">Em Aberto</th>
                  <th className="text-end">Em Atraso</th>
                  <th className="text-center">Parcelas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {providers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="acct-empty-cell">Nenhum fornecedor com despesas cadastradas.</td>
                  </tr>
                ) : providers.map(row => (
                  <tr key={row.provider_id} className="acct-tr-clickable" onClick={() => handleSelectProvider(row)}>
                    <td className="acct-td-name">{row.provider?.name || '—'}</td>
                    <td className="text-end">{formatCurrency(row.total)}</td>
                    <td className="text-end acct-td-paid">{formatCurrency(row.total_paid)}</td>
                    <td className="text-end acct-td-open">{formatCurrency(row.total_open)}</td>
                    <td className="text-end acct-td-overdue">{formatCurrency(row.total_overdue)}</td>
                    <td className="text-center">
                      <span className="acct-installments-badge">{row.count_paid}/{row.count}</span>
                    </td>
                    <td className="acct-td-arrow">→</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );

  const renderMobileListView = () => {
    const hasItems = providers.length > 0;
    return (
      <div>
        <div className="mapp-topbar">
          <span className="mapp-topbar-title">Contas a Pagar</span>
        </div>

        {loading ? (
          <div className="acct-loading">Carregando...</div>
        ) : (
          <>
            <div className="acct-cards-mobile">
              <div className="acct-card acct-card-total">
                <div className="acct-card-label">Total Geral</div>
                <div className="acct-card-value acct-card-value-sm">{formatCurrency(globalTotal)}</div>
              </div>
              <div className="acct-card acct-card-paid acct-card-payable">
                <div className="acct-card-label">Pago</div>
                <div className="acct-card-value acct-card-value-sm">{formatCurrency(globalPaid)}</div>
              </div>
              <div className="acct-card acct-card-open">
                <div className="acct-card-label">Em Aberto</div>
                <div className="acct-card-value acct-card-value-sm">{formatCurrency(globalOpen)}</div>
              </div>
              <div className="acct-card acct-card-overdue">
                <div className="acct-card-label">Em Atraso</div>
                <div className="acct-card-value acct-card-value-sm">{formatCurrency(globalOverdue)}</div>
              </div>
            </div>

            {!hasItems ? (
              <div className="mapp-empty">
                <div className="mapp-empty-icon">📤</div>
                <p>Nenhum fornecedor com despesas cadastradas.</p>
              </div>
            ) : providers.map(row => (
              <div key={row.provider_id} className="mapp-item" onClick={() => handleSelectProvider(row)}>
                <div className="mapp-item-body">
                  <div className="mapp-item-row1">
                    <span className="mapp-item-desc">{row.provider?.name || '—'}</span>
                    <span className="mapp-item-amount expense">{formatCurrency(row.total)}</span>
                  </div>
                  <div className="mapp-item-row2">
                    <span className="mapp-item-date">Pago: {formatCurrency(row.total_paid)}</span>
                    <span className="mapp-item-badge pendente">{row.count_paid}/{row.count} parcelas</span>
                  </div>
                  {parseFloat(row.total_overdue) > 0 && (
                    <div className="mapp-item-row2">
                      <span className="mapp-item-badge atrasada">Em atraso: {formatCurrency(row.total_overdue)}</span>
                    </div>
                  )}
                </div>
                <span className="acct-mobile-arrow">→</span>
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  // ─── DETAIL VIEW ─────────────────────────────────────────────────

  const renderDesktopDetailView = () => {
    const prov = selectedProvider;
    const hasActiveFilters = Object.values(filters).some(v => v !== '');
    return (
      <div>
        <div className="acct-breadcrumb">
          <button className="acct-btn-back" onClick={handleBack}>← Contas a Pagar</button>
          <span className="acct-breadcrumb-sep">/</span>
          <span className="acct-breadcrumb-current">{prov?.provider?.name || '—'}</span>
        </div>

        {renderSummaryCards(
          prov?.total, prov?.total_paid, prov?.total_open, prov?.total_overdue,
          prov?.count_paid, prov?.count_open
        )}

        <div className="acct-detail-toolbar">
          <div className="acct-filter-bar">
            {hasActiveFilters && <span className="acct-filter-active-dot" title="Filtros ativos" />}
            {renderFilters()}
          </div>
          <button className="acct-btn acct-btn-success" onClick={handleNewExpense}>
            + Nova Despesa
          </button>
        </div>

        {showForm && renderForm()}

        {!showForm && (
          <div className="acct-table-wrapper">
            <table className="acct-table">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th className="text-end">Valor</th>
                  <th>Vencimento</th>
                  <th>Pagamento</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="acct-empty-cell">Nenhuma despesa encontrada.</td>
                  </tr>
                ) : expenses.map(e => (
                  <tr key={e.id}>
                    <td className="acct-td-desc">{e.description}</td>
                    <td className="text-end acct-td-amount">{formatCurrency(e.amount)}</td>
                    <td>{formatDate(e.due_date)}</td>
                    <td>{formatDate(e.payment_date)}</td>
                    <td>
                      <span className={`acct-badge ${getStatusClass(e.status)}`}>{e.status}</span>
                    </td>
                    <td>
                      <button className="acct-btn acct-btn-sm acct-btn-edit" onClick={() => handleEdit(e)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {renderPagination()}
          </div>
        )}
      </div>
    );
  };

  const renderMobileDetailView = () => {
    const prov = selectedProvider;
    return (
      <div>
        <div className="mapp-topbar">
          <button className="acct-btn-back-mobile" onClick={handleBack}>←</button>
          <span className="mapp-topbar-title">{prov?.provider?.name || '—'}</span>
          <button className="mapp-filter-btn" onClick={() => setShowFilterModal(true)}>
            ⊞ Filtros
          </button>
        </div>

        <div className="acct-cards-mobile">
          <div className="acct-card acct-card-total">
            <div className="acct-card-label">Total</div>
            <div className="acct-card-value acct-card-value-sm">{formatCurrency(prov?.total)}</div>
          </div>
          <div className="acct-card acct-card-paid acct-card-payable">
            <div className="acct-card-label">Pago</div>
            <div className="acct-card-value acct-card-value-sm">{formatCurrency(prov?.total_paid)}</div>
          </div>
          <div className="acct-card acct-card-open">
            <div className="acct-card-label">Em Aberto</div>
            <div className="acct-card-value acct-card-value-sm">{formatCurrency(prov?.total_open)}</div>
          </div>
          <div className="acct-card acct-card-overdue">
            <div className="acct-card-label">Em Atraso</div>
            <div className="acct-card-value acct-card-value-sm">{formatCurrency(prov?.total_overdue)}</div>
          </div>
        </div>

        {showFilterModal && (
          <>
            <div className="mapp-sheet-overlay" onClick={() => setShowFilterModal(false)} />
            <div className="mapp-sheet">
              <div className="mapp-sheet-handle" />
              <div className="mapp-sheet-header">
                <span className="mapp-sheet-title">Filtrar Despesas</span>
                <button className="mapp-sheet-close" onClick={() => setShowFilterModal(false)}>✕</button>
              </div>
              <div className="mapp-sheet-body">
                {renderMobileFilters()}
              </div>
            </div>
          </>
        )}

        {expenses.length === 0 ? (
          <div className="mapp-empty">
            <div className="mapp-empty-icon">💸</div>
            <p>Nenhuma despesa encontrada</p>
          </div>
        ) : expenses.map(e => {
          const statusKey = getStatusClass(e.status);
          return (
            <div key={e.id} className="mapp-item" onClick={() => handleEdit(e)}>
              <div className={`mapp-item-accent ${statusKey}`} />
              <div className="mapp-item-body">
                <div className="mapp-item-row1">
                  <span className="mapp-item-desc">{e.description}</span>
                  <span className="mapp-item-amount expense">{formatCurrency(e.amount)}</span>
                </div>
                <div className="mapp-item-row2">
                  <span className="mapp-item-date">{formatDate(e.due_date)}</span>
                  <span className={`mapp-item-badge ${statusKey}`}>{e.status}</span>
                </div>
              </div>
            </div>
          );
        })}

        {renderPagination()}

        {showForm && renderForm()}

        <button className="mapp-fab expense" onClick={handleNewExpense}>+</button>
      </div>
    );
  };

  // ─── MAIN RENDER ─────────────────────────────────────────────────

  if (view === 'list') {
    return isMobile ? renderMobileListView() : renderDesktopListView();
  }
  return isMobile ? renderMobileDetailView() : renderDesktopDetailView();
}

export default AccountsPayablePage;
